'use client'

import { useId, useRef, useState } from 'react'
import { getTokensConfig, getTokens, getResolvedValue } from '@systhemaui/core'

const { breakpoints } = getTokensConfig() as { breakpoints: Record<string, string> }
const { responsiveSizing } = getTokens()

import { Swiper, SwiperSlide } from 'swiper/react'
import { Keyboard, Navigation, Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'

import { getSysthemaConfigSync } from '@systhemaui/core'
const config = getSysthemaConfigSync()

import type { SectionProps } from '@systhemaui/next'
import { Section, Card, Heading, Paragraph, Icon } from '@systhemaui/next'

import clsx from 'clsx'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faStar } from '@fortawesome/free-solid-svg-icons'

function constructFaSvg(icon: IconDefinition) {
  if (!icon || !icon.icon || !icon.icon[0] || !icon.icon[1] || !icon.icon[4]) {
    return ''
  }

  const viewBox = `${icon.icon[0]} ${icon.icon[1]}`
  const path = icon.icon[4]

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewBox}"><path fill="currentColor" d="${path}"/></svg>`
}

export interface TestimonialItem {
  name: string
  content: string
  rating?: number
}

export interface TestimonialsData {
  title?: string
  description?: string
  items: TestimonialItem[]
}

type Props = Omit<SectionProps, 'content'> & {
  data: TestimonialsData
}

const Testimonials: React.FC<Props> = ({ data, ...props }) => {
  const id = useId()
  const testimonialsId = `${id.replace(/[^a-zA-Z0-9]/g, '')}`

  const navigationPrevRef = useRef<HTMLButtonElement>(null)
  const navigationNextRef = useRef<HTMLButtonElement>(null)
  const paginationRef = useRef<HTMLDivElement>(null)
  const [paginationState, setPaginationState] = useState({ current: 1, total: 1 })

  const handleSwiper = (swiper: SwiperType) => {
    // Update pagination display when active index changes
    swiper.on('slideChange', () => {
      setPaginationState({
        current: swiper.activeIndex + 1,
        total: swiper.slides.length,
      })
    })

    // Initialize pagination state
    setPaginationState({
      current: swiper.activeIndex + 1,
      total: swiper.slides.length,
    })

    // Use rAF to ensure navigation button refs are populated
    requestAnimationFrame(() => {
      if (navigationPrevRef.current && navigationNextRef.current) {
        if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
          swiper.params.navigation.prevEl = navigationPrevRef.current
          swiper.params.navigation.nextEl = navigationNextRef.current
          swiper.navigation.init()
          swiper.navigation.update()
        }
      }
    })
  }

  const { theme = 'default', layoutBackground = 'base', ...rest } = props

  return (
    <Section theme={theme} layoutBackground={layoutBackground} className="gallery" {...rest}>
      {data.title && <Heading.h2 className="text-center">{data.title}</Heading.h2>}
      {data.description && <Paragraph className="text-center">{data.description}</Paragraph>}
      <Swiper
        id={testimonialsId}
        className="mt-(--gap-md) [&_.swiper-slide]:flex [&_.swiper-slide]:grow"
        modules={[Keyboard, Navigation, Pagination]}
        slidesPerView={1}
        spaceBetween={parseInt(
          (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'sm') as string) ?? '24',
        )}
        loop={false}
        grabCursor={true}
        autoHeight={true}
        keyboard={{
          enabled: true,
          onlyInViewport: true,
        }}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 250,
          thresholdDelta: 10,
          releaseOnEdges: true,
        }}
        navigation={true}
        pagination={false}
        breakpoints={{
          [parseInt(breakpoints?.md as string) ?? 640]: {
            slidesPerView: 2,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'md') as string) ?? '24',
            ),
          },
          [parseInt(breakpoints?.lg as string) ?? 1192]: {
            slidesPerView: 3,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'lg') as string) ?? '24',
            ),
          },
        }}
        onSwiper={handleSwiper}
      >
        {data.items.map((item) => (
          <SwiperSlide key={item.name}>
            <Card className="column" disableAnimation>
              <Heading.h6 disableAnimation>{item.name}</Heading.h6>
              <div className="flex items-center justify-start gap-2.5">
                {Array.from({ length: item.rating ?? 5 }).map((_, i) => (
                  <Icon
                    key={i}
                    as="span"
                    dangerouslySetInnerHTML={{ __html: constructFaSvg(faStar) }}
                  />
                ))}
              </div>
              <Paragraph className="italic" disableAnimation>
                {item.content}
              </Paragraph>
            </Card>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="aos gallery-navigation animate-fadeinup mt-6">
        <div className="gallery-navigation-pagination" ref={paginationRef}>
          {paginationState.current} / {paginationState.total}
        </div>
        <div className="gallery-navigation-buttons">
          <button
            ref={navigationPrevRef}
            className={clsx(
              'gallery-navigation-button-prev',
              typeof config.packages?.react === 'object' && config.packages?.react?.enabled
                ? config.packages?.react?.transitionClasses
                : undefined,
            )}
            aria-controls={`${testimonialsId}-swiper`}
            aria-label="Previous Slide"
            tabIndex={0}
          >
            <svg
              className="gallery-navigation-button-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path
                fill="currentColor"
                d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H109.3l105.3-105.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
              />
            </svg>
            <span className="sr-only!">Previous</span>
          </button>
          <button
            ref={navigationNextRef}
            className={clsx(
              'gallery-navigation-button-next',
              typeof config.packages?.react === 'object' && config.packages?.react?.enabled
                ? config.packages?.react?.transitionClasses
                : undefined,
            )}
            aria-controls={`${testimonialsId}-swiper`}
            aria-label="Next Slide"
            tabIndex={0}
          >
            <span className="sr-only!">Next</span>
            <svg
              className="gallery-navigation-button-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path
                fill="currentColor"
                d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224H32c-17.7 0-32 14.3-32 32s14.3 32 32 32h306.7L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"
              />
            </svg>
          </button>
        </div>
      </div>
    </Section>
  )
}

export default Testimonials
